/**
 * Composable for handling SSE streaming chat responses
 */
export function useChatStreaming() {
  /**
   * Process a streaming chat response from the server
   * @param {Response} response - Fetch API Response object with streaming body
   * @param {Object} callbacks - Event handlers for different stream events
   * @param {Function} callbacks.onContent - Called when content chunk received (content: string)
   * @param {Function} callbacks.onToolCall - Called when tool call starts (tools: array)
   * @param {Function} callbacks.onError - Called on error event (message: string)
   * @param {Function} callbacks.onDone - Called when stream completes
   * @returns {Promise<string>} - Returns the complete streamed content
   */
  async function processStreamingResponse(response, callbacks = {}) {
    const {
      onContent = () => {},
      onToolCall = () => {},
      onError = () => {},
      onDone = () => {},
    } = callbacks;

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullContent = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;

          try {
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case "content":
                fullContent += event.content;
                onContent(event.content);
                break;
              case "tool_call":
                fullContent = "";
                onToolCall(event.tools, event.status);
                break;
              case "done":
                onDone();
                break;
              case "error":
                const errorMsg = event.message || "An error occurred";
                onError(errorMsg);
                fullContent += `\n\n**Error:** ${errorMsg}`;
                break;
            }
          } catch (parseError) {
            console.error("Malformed JSON in stream:", line, parseError);
          }
        }
      }

      return fullContent;
    } catch (error) {
      throw new Error(`Stream processing failed: ${error.message}`);
    }
  }

  return { processStreamingResponse };
}
